import React, { useContext, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { compose } from "redux";
import { CostOfStatusQuoChart, FinancialCards, Statistics } from "./components";
import { DiscoveryFooter, DiscoveryTitleBar } from "../../components/Discovery";
import { useString as s } from "../../components/StringProvider";
import Heading from "../../components/Heading";
import PropTypes from "prop-types";
import {
  hasPermission,
  selectDiscoveryCurrency,
  selectDiscoveryId,
  selectDiscoveryName,
  selectDiscoverySelectedKPIs,
  selectRequestData
} from "../../store/reducers";
import { Dropdown, Space } from "antd";
import NewButton from "../../components/NewButton";
import MobileReportTitle from "../Report/components/MobileReportTitle";
import {
  FinishDiscoveryButton,
  GoToROIButton,
  RequestEvaluationButton
} from "../../components/DiscoveryButtons";
import { useMobileMediaQuery } from "../../components/Responsive";
import { Icon } from "../../components";
import Permissions from "../../utils/permissions";
import { RadioButton, RadioGroup } from "../../components/Radio";
import {
  CostOfStatusQuoChartContext,
  CostOfStatusQuoChartModes
} from "./components/CostOfStatusQuoChartProvider";
import actionTypes from "../../store/actionTypes";
import { Phases } from "../../utils/phases";
import Menu, { MenuItem } from "../../components/Menu";
import ViewValueHypothesisKPIsBy from "../../utils/view-value-hypothesis-kpis-by";
import { ValueHypothesisContext } from "./components/ValueHypothesisProvider";

const FooterButtons = ({ reportMode, discoveryId }) => {
  return reportMode ? null : (
    <DiscoveryFooter>
      <GoToROIButton discoveryId={discoveryId} />
      <RequestEvaluationButton />
      <FinishDiscoveryButton />
    </DiscoveryFooter>
  );
};

const ValueHypothesisReport = ({
  reportId,
  discoveryId,
  discoveryName,
  discoveryKPIs,
  currency,
  canSeeLeadGeneration,
  template
}) => {
  const isMobile = useMobileMediaQuery();
  const [isShowAllChallenges, setShowAllChallenges] = useState(false);
  const reportMode = !!reportId;
  const hideAllChallenges = s(
    "discovery.opportunity.page.show_challenges.text",
    "Show all challenges"
  );
  const showAllChallenges = s(
    "discovery.opportunity.page.hide_challenges.text",
    "Hide all challenges"
  );
  const showAllHypothesesButton = s(
    "report.valueHypothesis.showAllHypotheses",
    "Show all hypotheses"
  );
  const hideHypothesesButton = s(
    "report.valueHypothesis.hideHypotheses",
    "Hide hypotheses"
  );
  const header = s(
    "discovery.opportunity.page.header",
    "{valueHypothesis} for {discoveryName}",
    { discoveryName }
  );
  const subheader = s(
    "discovery.opportunity.page.subheader",
    "How {companyName} helps companies like you"
  );
  const subheaderReport = s(
    "report.valueHypothesis.subheader",
    "How {companyName} helps companies like you"
  );
  const costOfStatusQuoChartHeader = s(
    "discovery.opportunity.page.header2",
    "Cost of Status Quo"
  );
  const costOfStatusQuoChartSubheader = s(
    "discovery.opportunity.page.subheader2",
    "The cost to you if you choose not to use the {companyName} platform"
  );
  const benefitSummaryChartHeader = s(
    "discovery.opportunity.page.benefitSummaryChart.header",
    "Benefit Summary"
  );
  const benefitSummaryChartSubheader = s(
    "discovery.opportunity.page.benefitSummaryChart.subheader",
    "The annual benefits to be gained after adopting the new solution"
  );
  const [showAllHypotheses, setShowAllHypotheses] = useState(false);

  // Get sort state from ValueHypothesisProvider
  const { sortBy, updateSortBy } = useContext(ValueHypothesisContext);
  const { chartMode, updateChartMode } = useContext(
    CostOfStatusQuoChartContext
  );

  const showChartControls =
    (!reportMode || (reportMode && template !== Phases.ROI)) &&
    !canSeeLeadGeneration;

  // Sort By dropdown strings
  const sortByText = s("discovery.valueHypothesis.page.sortBy.text", "SORT BY");
  const valueText = s("discovery.valueHypothesis.page.sortBy.value", "Value");
  const importanceText = s(
    "discovery.valueHypothesis.page.sortBy.importance",
    "Importance"
  );

  const handleSortByChange = (newValue) => {
    updateSortBy(newValue);
  };

  const sortByMenu = (
    <Menu>
      <MenuItem
        key={ViewValueHypothesisKPIsBy.VALUE}
        onClick={() => handleSortByChange(ViewValueHypothesisKPIsBy.VALUE)}
        data-cy={"sort-by-value"}
      >
        {valueText}
      </MenuItem>
      <MenuItem
        key={ViewValueHypothesisKPIsBy.IMPORTANCE}
        onClick={() => handleSortByChange(ViewValueHypothesisKPIsBy.IMPORTANCE)}
        data-cy={"sort-by-importance"}
      >
        {importanceText}
      </MenuItem>
    </Menu>
  );

  const SortByDropdown = ({ dataCy }) => (
    <SortByContainer>
      <Heading level="h5">{sortByText}</Heading>
      <Dropdown overlay={sortByMenu} trigger="click">
        <NewButton
          type={"text"}
          className="ant-dropdown-link"
          onClick={(e) => e.preventDefault()}
          data-cy={dataCy}
        >
          {sortBy === ViewValueHypothesisKPIsBy.VALUE
            ? valueText
            : importanceText}
          <Icon name={"angleDown"} />
        </NewButton>
      </Dropdown>
    </SortByContainer>
  );

  const onToggleAllChallenges = () => {
    setShowAllChallenges(!isShowAllChallenges);
  };

  const onToggleAllHypotheses = () => {
    setShowAllHypotheses(!showAllHypotheses);
  };

  const handleModeChange = (e) => {
    updateChartMode(e.target.value);
  };

  const ChartModeButtons = () =>
    showChartControls ? (
      <RadioGroup
        onChange={handleModeChange}
        value={chartMode}
        style={
          isMobile
            ? { marginTop: 16, marginBottom: 24 }
            : { marginTop: 16, marginBottom: 8 }
        }
      >
        <RadioButton
          value={CostOfStatusQuoChartModes.BENEFIT_SUMMARY}
          data-cy={"benefit-summary-chart-button"}
        >
          {benefitSummaryChartHeader}
        </RadioButton>
        <RadioButton
          value={CostOfStatusQuoChartModes.COST_OF_STATUS_QUO}
          data-cy={"cost-of-status-quo-chart-button"}
        >
          {costOfStatusQuoChartHeader}
        </RadioButton>
      </RadioGroup>
    ) : null;

  return isMobile ? (
    <>
      <MobileReportTitle
        header={header}
        subheader={reportId ? subheaderReport : subheader}
        headerLevel={"h3"}
      />
      <Space direction={"vertical"} size={16} style={{ marginBottom: "40px" }}>
        <Statistics currency={currency} kpis={discoveryKPIs} />
        {!canSeeLeadGeneration && (
          <SortByDropdown dataCy={"sort-by-dropdown-mobile"} />
        )}
        <FinancialCards
          isShowAllChallenges={isShowAllChallenges}
          showAllHypotheses={showAllHypotheses}
          reportId={reportId}
        />
        {discoveryKPIs.length > 4 ? (
          <ButtonContainer>
            <NewButton type={"text"} onClick={onToggleAllHypotheses}>
              {showAllHypotheses
                ? hideHypothesesButton
                : showAllHypothesesButton}
            </NewButton>
          </ButtonContainer>
        ) : null}
      </Space>
      <ChartModeButtons />
      <MobileReportTitle
        header={
          chartMode === CostOfStatusQuoChartModes.COST_OF_STATUS_QUO
            ? costOfStatusQuoChartHeader
            : benefitSummaryChartHeader
        }
        subheader={
          chartMode === CostOfStatusQuoChartModes.COST_OF_STATUS_QUO
            ? costOfStatusQuoChartSubheader
            : benefitSummaryChartSubheader
        }
        headerLevel={"h2"}
      />
      <CostOfStatusQuoChart
        kpis={discoveryKPIs}
        currency={currency}
        reportMode={reportMode}
      />
      <FooterButtons reportMode={reportMode} discoveryId={discoveryId} />
    </>
  ) : (
    <Wrapper>
      <WrapperInnerTop>
        <WrapperInnerHeader>
          <DiscoveryTitleBar>
            <Heading level={reportMode ? "h1" : "h2"}>{header}</Heading>
          </DiscoveryTitleBar>
          <SubHeaderContainer>
            <div>{reportId ? subheaderReport : subheader}</div>
            {!canSeeLeadGeneration && (
              <ControlsContainer>
                <SortByDropdown dataCy={"sort-by-dropdown"} />
                <NewButton
                  type={"secondaryDarkText"}
                  onClick={onToggleAllChallenges}
                >
                  <Icon
                    name={isShowAllChallenges ? "collapse" : "expand"}
                    size={"large"}
                  />
                  {isShowAllChallenges ? showAllChallenges : hideAllChallenges}
                </NewButton>
              </ControlsContainer>
            )}
          </SubHeaderContainer>
        </WrapperInnerHeader>
        <WrapperInnerBody>
          <WrapperInnerTopLeft>
            <FinancialCards
              isShowAllChallenges={isShowAllChallenges}
              reportId={reportId}
              showAllHypotheses={true}
            />
          </WrapperInnerTopLeft>
          <WrapperInnerTopRight>
            <Statistics currency={currency} kpis={discoveryKPIs} />
          </WrapperInnerTopRight>
        </WrapperInnerBody>
      </WrapperInnerTop>
      <WrapperInnerBottom reportMode={reportMode}>
        <ChartModeButtons />
        <Header className="up-cosq-title" style={{ marginTop: 24 }}>
          <Heading level={"h2"} data-cy={"chart-header"}>
            {chartMode === CostOfStatusQuoChartModes.COST_OF_STATUS_QUO
              ? costOfStatusQuoChartHeader
              : benefitSummaryChartHeader}
          </Heading>
          <span data-cy={"chart-subheader"}>
            {chartMode === CostOfStatusQuoChartModes.COST_OF_STATUS_QUO
              ? costOfStatusQuoChartSubheader
              : benefitSummaryChartSubheader}
          </span>
        </Header>
        <CostOfStatusQuoChart
          kpis={discoveryKPIs}
          currency={currency}
          reportMode={reportMode}
        />
        <FooterButtons reportMode={reportMode} discoveryId={discoveryId} />
      </WrapperInnerBottom>
    </Wrapper>
  );
};

ValueHypothesisReport.propTypes = {
  reportId: PropTypes.string,
  canSeeFinishHere: PropTypes.bool,
  canSeeROIPage: PropTypes.bool
};

const ButtonContainer = styled.div`
  width: 100%;
  text-align: center;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;

  @media print {
    page-break-before: always;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const WrapperInnerHeader = styled.div`
  width: 100%;
`;

const SubHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;

  @media screen and (max-width: 1160px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SortByContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: baseline;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
`;

const WrapperInnerBody = styled.div`
  width: 100%;
  display: flex;
  gap: 32px;
  flex-direction: column;

  @media screen and (min-width: 1160px) {
    flex-direction: row;
  }
`;

const WrapperInnerTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const WrapperInnerTopRight = styled.div`
  min-width: 284px;
`;

const WrapperInnerTopLeft = styled.div``;

const WrapperInnerBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

WrapperInnerBottom.propTypes = {
  reportMode: PropTypes.bool
};

WrapperInnerBottom.defaultProps = {
  reportMode: true
};

const mapStateToProps = (state) => ({
  discoveryId: selectDiscoveryId(state),
  discoveryName: selectDiscoveryName(state),
  currency: selectDiscoveryCurrency(state),
  discoveryKPIs: selectDiscoverySelectedKPIs(state),
  canSeeLeadGeneration: hasPermission(state, Permissions.SEE_LEAD_GENERATION),
  template: selectRequestData(
    state,
    actionTypes.DISCOVERY_SET_REPORT_TEMPLATE_SUCCESS
  )
});

export default compose(connect(mapStateToProps))(ValueHypothesisReport);
